<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { Capacitor } from '@capacitor/core';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@stores/auth';
import { url } from '@/config/urls';
import { modalUtil } from '@functions/modalUtil.js';
import AddBarcodeScanDeviceConfirmModal from './modal/AddBarcodeScanDeviceConfirmModal.vue';
import RegisteredBarcodeScanDeviceConfirmModal from './modal/RegisteredBarcodeScanDeviceConfirmModal.vue';
import axios from 'axios';
import { apiError } from '@functions/apiError';
import ApiErrorModal from './ApiErrorModal.vue';
import { useScanNavigationStore } from '@stores/scanNavigation';
import deviceService from '@/services/deviceService.js';
import pushNotificationService from '@/services/pushNotificationService.js';
import { useSideMenuStore } from '@stores/sidemenu.js';
import { useServiceApplyStore } from '@stores/serviceApply';
import ToUserSettingConfirmModal from './modal/ToUserSettingConfirmModal.vue';
import { setPendingNav } from '@/router/index.js';

// props定義
const props = defineProps({
    loginUrl: {
        type: String,
        default: url.login
    },
    serviceApplyGetApiUrl: {
        type: String,
        default: url.api.serviceApply.get
    },
    orderScanPageUrl: {
        type: String,
        default: url.page.orderScan
    },
    returnScanPageUrl: {
        type: String,
        default: url.page.returnScan
    },
    mobileDeviceUpdateApiUrl : {
        type: String,
        default: url.api.mobileDevices.update
    },
    mobileDeviceListApiUrl: {
        type: String,
        default: url.api.mobileDevices.list
    },
    mobileDeviceAddApiUrl: {
        type: String,
        default: url.api.mobileDevices.add
    },
});

const auth = useAuthStore();
const navScanNav = useScanNavigationStore();
const sideMenuStore = useSideMenuStore();
const serviceApplyStore = useServiceApplyStore();
const enifClub = computed(() => serviceApplyStore.enifClub);
const barcodeScan = computed(() => serviceApplyStore.barcodeScan);
const route = useRoute();
const router = useRouter();
const { showModal } = modalUtil();

const isAuthenticated = computed(() => auth.isAuthenticated);
const isReturnMode = computed(() => route.meta.requiresReturnTerms === true);

const user = computed(() => auth.user || {
    orderFlg: null,
    returnRequestFlg: null,
    isPermittedOrder: false,
    isOrderTermsAgreed: false,
    isPermittedReturnRequest: false,
    isReturnTermsAgreed: false,
    barcodeScanApplyPermissionFlg: null
});

// ネイティブアプリ判定（スキャンボタンはアプリ版限定）
const isNativeApp = Capacitor.isNativePlatform();

// スキャンボタン表示判定（ポータルユーザーテーブルのバーコードスキャン利用可否フラグが0の場合は非表示）
const showOrderScanButton = computed(() => isNativeApp && user.value.orderFlg === 0 && user.value.barcodeScanApplyPermissionFlg === 1 && !isReturnMode.value);
const showReturnScanButton = computed(() => isNativeApp && user.value.returnRequestFlg === 0 && user.value.barcodeScanApplyPermissionFlg === 1 && isReturnMode.value);
const showScanButton = computed(() => showOrderScanButton.value || showReturnScanButton.value);

const barcodeScanDeviceList = ref([]);
const totalNum = ref(0);
const isProcessing = ref(false);
const errorMessages = ref([]);

const ORDER_MENU_PATHS = [
    '/order-search',
    '/order-favorite',
    '/order-favorite-detail',
    '/order-history',
    '/order-cart',
];

// 現在のルートパスを取得してアクティブ状態を判定
const isActive = (path) => {
    return !sideMenuStore.isSideMenuOpen && route.path === path;
};

const isOrderMenuActive = computed(() => {
    return !sideMenuStore.isSideMenuOpen && ORDER_MENU_PATHS.includes(route.path);
});

const closeMenuIfOpen = () => {
    sideMenuStore.close();
};

// スキャンボタン押下時
const onClickScan = async() => {
    closeMenuIfOpen();
    if (isProcessing.value) return;
    isProcessing.value = true;

    // バーコードスキャンが利用できない場合はユーザー管理画面へ誘導するモーダルを表示する
    if (!barcodeScan.value?.isAvailable) {
        isProcessing.value = false;
        showModal('toUserSettingConfirmModal');
        return;
    }

    // 発注スキャン：利用規約未同意の場合
    if (!isReturnMode.value && !user.value.isOrderTermsAgreed) {
        isProcessing.value = false;
        setPendingNav({ path: props.orderScanPageUrl });
        showModal('orderTermsModal');
        return;
    }

    // 返品スキャン：利用規約未同意の場合
    if (isReturnMode.value && !user.value.isReturnTermsAgreed) {
        isProcessing.value = false;
        setPendingNav({ path: props.returnScanPageUrl });
        showModal('returnTermsModal');
        return;
    }

    isProcessing.value = false;
    await resumeDeviceCheck(isReturnMode.value);
};

// 端末登録チェック〜スキャン画面遷移（規約同意後の再開にも共用）
const resumeDeviceCheck = async (isReturn) => {
    isProcessing.value = true;
    try {
        await getBarcodeScanDevices();

        const currentUuid = await deviceService.getDeviceUUID();
        const deviceExists = hasSameUuid(currentUuid);

        // すでに登録済みの端末であればバーコードスキャン機能ページに遷移する
        if (deviceExists) {
            // 最終スキャン日時を更新する
            const pushToken = await pushNotificationService.getPushToken();
            await axios.post(props.mobileDeviceUpdateApiUrl, {
                uuid: currentUuid
                , scanUseFlg: '1'
                , pushToken
            });
            if (isReturn) {
                navScanNav.enableReturnScan();
                await router.push(props.returnScanPageUrl);
            } else {
                navScanNav.enableOrderScan();
                await router.push(props.orderScanPageUrl);
            }
        // 未登録の端末 && 3台登録されている場合
        } else if (totalNum.value >= 3) {
            showModal('registeredBarcodeScanDeviceConfirmModal');
        // 未登録の端末 && 3台未満
        } else {
            showModal('addBarcodeScanDeviceConfirmModal');
        }
    } catch(error) {
        apiError(error, errorMessages);
    } finally {
        isProcessing.value = false;
    }
};

// 規約同意後、発注スキャンのデバイスチェックを再開
watch(() => navScanNav.pendingOrderScanResume, async (val) => {
    if (!val) return;
    navScanNav.clearOrderScanResume();
    await resumeDeviceCheck(false);
});

// 規約同意後、返品スキャンのデバイスチェックを再開
watch(() => navScanNav.pendingReturnScanResume, async (val) => {
    if (!val) return;
    navScanNav.clearReturnScanResume();
    await resumeDeviceCheck(true);
});

// バーコードスキャン端末一覧取得処理
const getBarcodeScanDevices = async() => {
    try {
        const response = await axios.get(props.mobileDeviceListApiUrl, {
            params: { onlyBarcodeScanDevices: true }
        });
        barcodeScanDeviceList.value = response.data.response.mobileDeviceList ?? [];
        totalNum.value = parseInt(response.data.response.totalNum);
    } catch(error) {
        apiError(error, errorMessages);
    }
}

// スキャンしたuuidと登録済みのuuidが一緒か
const hasSameUuid = (scannedUuid) => {
    if(!scannedUuid || !barcodeScanDeviceList.value) return false;

    return barcodeScanDeviceList.value.some(device => device.uuid === scannedUuid);
}

const getPlatform = (platform) => {
    if (platform === 'ios') return 'iOS';
    if (platform === 'android') return 'Android';
    return 'Web';
}

// バーコードスキャン利用
const registerBarcodeScanDevice = async() => {
    try {
        // 最新状態取得
        await getBarcodeScanDevices();

        // デバイス情報取得
        const deviceInfo = await deviceService.getDeviceInfo();
        const pushToken = await pushNotificationService.getPushToken();
        const platform = getPlatform(deviceInfo.platform);

        // UUIDの下4桁取得
        const uuidSuffix = (deviceInfo.uuid || '').slice(-4);

        // UUIDの存在チェック
        const deviceExists = hasSameUuid(deviceInfo.uuid);

        const request = {
            uuid: deviceInfo.uuid,
            platform: (platform || '').substring(0, 10),
            model:`${platform} ${uuidSuffix}`.substring(0, 50),
            pushToken,
            scanUseFlg: '1',
            doUpdateScannedAt: true,
        };

        // 登録済みの場合、更新
        if (deviceExists) {
            await axios.post(props.mobileDeviceUpdateApiUrl, request);
        // 未登録の場合、新規追加
        } else {
            await axios.post(props.mobileDeviceAddApiUrl, request);
        }

        // スキャン画面遷移
        if (isReturnMode.value) {
            navScanNav.enableReturnScan();
            await router.push('/return-scan');
        } else {
            navScanNav.enableOrderScan();
            await router.push(props.orderScanPageUrl);
        }
    } catch(error) {
        apiError(error, errorMessages);
    }
}

onMounted(() => {
    serviceApplyStore.fetchServiceApply(props.serviceApplyGetApiUrl);
    getBarcodeScanDevices();
})
</script>

<template>
<div v-if="isAuthenticated" class="com_app_nav1" :class="{ has_scan1: showScanButton }">
    <ul class="nav1">
        <li>
            <router-link to="/" :class="{ on: isActive('/') }" @click="closeMenuIfOpen">
                <img :src="isActive('/') 
                ? '/front/assets/common/img/icon_home1_on.png' 
                : '/front/assets/common/img/icon_home1.png'" 
                alt=""
                >
                <span>ホーム</span>
            </router-link>
        </li>
        <li>
            <router-link to="/order-results" :class="{ on: isActive('/order-results') }" @click="closeMenuIfOpen">
                <img :src="isActive('/order-results')
                ? '/front/assets/common/img/icon_order_result1_on.png'
                : '/front/assets/common/img/icon_order_result1.png'" 
            alt=""
            >
            <span>注文結果</span>
        </router-link>
    </li>
    <li v-if="user.orderFlg === 0 || user.orderFlg === 1 || user.orderFlg === 2">
        <router-link to="/order-search" :class="{ on: isOrderMenuActive }" @click="closeMenuIfOpen">
            <template v-if="user.isPermittedOrder">
            <img :src="isOrderMenuActive
            ? '/front/assets/common/img/icon_order1_on.png'
            : '/front/assets/common/img/icon_order1.png'" 
            alt=""
            >
            <span>発注</span>
            </template>
            <template v-else>
                <img :src="isOrderMenuActive
            ? '/front/assets/common/img/icon_stock_inquiry1_on.png'
            : '/front/assets/common/img/icon_stock_inquiry1.png'" 
            alt=""
            >
            <span>在庫問合せ</span>
            </template>
        </router-link>
    </li>
    <li>
        <a href="#" @click.prevent="sideMenuStore.toggle" :class="{ on: sideMenuStore.isSideMenuOpen }">
            <img :src="sideMenuStore.isSideMenuOpen
            ? '/front/assets/common/img/icon_menu1_on.png'
            : '/front/assets/common/img/icon_menu1.png'"
            alt=""
            >
            <span>メニュー</span>
        </a>
    </li>
    </ul>
    <!-- 発注スキャン -->
    <button href="#" class="btn_scan1" @click.prevent="onClickScan" v-if="showOrderScanButton"
        :disabled="isProcessing">
        <img src="/front/assets/common/img/btn_scan1.png" alt="発注スキャン">
    </button>

    <!-- 返品スキャン（isReturnModeのときだけ） -->
    <button href="#" class="btn_scan1" @click.prevent="onClickScan" v-else-if="showReturnScanButton"
        :disabled="isProcessing">
        <img src="/front/assets/common/img/btn_scan4.png" alt="返品スキャン">
    </button>
</div>
<AddBarcodeScanDeviceConfirmModal @registerBarcodeScanDevice="registerBarcodeScanDevice"/>
<RegisteredBarcodeScanDeviceConfirmModal />
<ToUserSettingConfirmModal />
<ApiErrorModal :messages="errorMessages" />
</template>

<style scoped>
/* 必要に応じてスタイルを追加 */
</style>